import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { settingsAPI } from '../utils/api';

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dismissedPromo, setDismissedPromo] = useState('');
  const location = useLocation();
  const isPortalRoute = /^\/(admin|photographer|client)(\/|$)/.test(location.pathname);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* Mirror the portal-route flag onto <body> so the page background can be
     themed. The body sits outside every route wrapper, so CSS alone cannot
     tell a portal page from a public one — and theming body globally is
     exactly what once turned the public marketing site dark for visitors whose
     OS is in dark mode. */
  useEffect(() => {
    document.body.classList.toggle('portal-route', isPortalRoute);
    return () => document.body.classList.remove('portal-route');
  }, [isPortalRoute]);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Dismissal is keyed to the message, so a new promo reappears for anyone
  // who closed the previous one.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('urp_promo_dismissed');
      if (stored) setDismissedPromo(stored);
    } catch (e) {
      // Privacy browsers block storage; showing the bar is the safe default.
    }
  }, []);

  // Re-check user status on every navigation (catches login/logout)
  useEffect(() => {
    checkUserStatus();
  }, [location]);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      // Public settings are wrapped in { success, data } — unwrap so fields
      // like homepagePromo can be read directly.
      setSettings(response.data?.data || response.data || null);
    } catch (error) {
      // Settings endpoint doesn't exist yet - use defaults
      console.log('Using default settings');
      setSettings(null);
    }
  };

  const checkUserStatus = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('clientToken');
    const userData = localStorage.getItem('user');
    
    if (token && userData && userData !== 'undefined' && userData !== 'null') {
      try {
        const parsed = JSON.parse(userData);
        if (parsed && parsed.role) setUser(parsed);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user'); // clear corrupt data
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('clientToken');
    localStorage.removeItem('user');
    setUser(null);
    closeMobileMenu();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Promo strip. It lives inside the fixed <nav> on purpose: .navbar is
  // position:fixed with an opaque background at z-index 1000, so anything
  // rendered at the top of normal page flow (as this previously was, inside
  // Home) sits underneath it and is never visible.
  const promo = settings?.homepagePromo;
  const promoText = promo?.text || '';
  const onHomepage = location.pathname === '/';
  const showPromo = onHomepage && !!(promo?.enabled && promoText) && dismissedPromo !== promoText;

  const dismissPromo = () => {
    setDismissedPromo(promoText);
    try { window.localStorage.setItem('urp_promo_dismissed', promoText); } catch (e) { /* non-fatal */ }
  };

  return (
    /* `navbar--themed` is added ONLY on authenticated portal routes, so the
       navbar can follow dark mode inside the portals while the public site
       stays light regardless of the visitor's OS. The navbar is shared between
       both, and theming it unconditionally is precisely how dark mode leaked
       to the public site on 2026-07-26. */
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}${isPortalRoute ? ' navbar--themed' : ''}`}>
      {showPromo && (
        <div className="promo-bar" role="region" aria-label="Current offer">
          <div className="promo-bar-inner">
            <span className="promo-dot" aria-hidden="true"></span>
            <p className="promo-text">{promoText}</p>
            {promo.linkUrl && promo.linkText && (
              <Link to={promo.linkUrl} className="promo-link" onClick={closeMobileMenu}>{promo.linkText}</Link>
            )}
            <button type="button" className="promo-close" onClick={dismissPromo} aria-label="Dismiss offer">&times;</button>
          </div>
        </div>
      )}
      <div className="navbar-container">
        <Link to="/" className="logo" onClick={closeMobileMenu}>
          <img src="/logo.png" alt="Utah Realty Photos" className="logo-img" />
        </Link>
        
        <button 
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <li><Link to="/" className={isActive('/')} onClick={closeMobileMenu}>Home</Link></li>
          <li><Link to="/portfolio" className={isActive('/portfolio')} onClick={closeMobileMenu}>Portfolio</Link></li>
          <li><Link to="/services" className={isActive('/services')} onClick={closeMobileMenu}>Services</Link></li>
          <li><Link to="/pricing" className={isActive('/pricing')} onClick={closeMobileMenu}>Pricing</Link></li>
          <li><Link to="/blog" className={isActive('/blog')} onClick={closeMobileMenu}>Blog</Link></li>
          <li><Link to="/contact" className={isActive('/contact')} onClick={closeMobileMenu}>Contact</Link></li>
          <li><Link to="/book" className={`${isActive('/book')} nav-cta-btn`} onClick={closeMobileMenu}>Book Now</Link></li>
          
          {/* Dynamic login/dashboard links based on user role */}
          {!user && (
            <li><Link to="/client/login" className={`${isActive('/client/login')} client-login-btn`} onClick={closeMobileMenu}>Client Login</Link></li>
          )}
          
          {user && user.role === 'admin' && (
            <>
              <li><Link to="/admin" className={isActive('/admin')} onClick={closeMobileMenu}>Dashboard</Link></li>
              <li><button onClick={handleLogout} className="logout-nav-btn">Logout</button></li>
            </>
          )}
          
          {user && user.role === 'photographer' && (
            <>
              <li><Link to="/photographer" className={isActive('/photographer')} onClick={closeMobileMenu}>Dashboard</Link></li>
              <li><button onClick={handleLogout} className="logout-nav-btn">Logout</button></li>
            </>
          )}
          
          {user && user.role === 'client' && (
            <>
              <li><Link to="/client/portal" className={isActive('/client/portal')} onClick={closeMobileMenu}>My Account</Link></li>
              <li><button onClick={handleLogout} className="logout-nav-btn">Logout</button></li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
